import { RESERVED_PLATFORM_PREFIXES } from "@zion/sdk-contracts";
//#region src/api-routes.ts
/**
* Shared utilities for `apiPaths`-based L7 routing.
*
* `apiPaths` is the universal declarative contract:
*   - `@zion/release-engine-v3` (CP) uses them to validate published bundles.
*   - `@zion/zion-operator` (DP) uses them to emit Gateway-API HTTPRoute
*     rules on the public DP gateway listener.
*
* Single source of truth — drift between consumers becomes a real security
* incident (e.g. CP accepts a path the operator rejects, leaving a fixture
* stranded; or the operator accepts something CP didn't validate, opening
* a header-spoofing or reserved-path-bypass vulnerability).
*
* All functions are pure — no I/O, no clock, no globals. Tested to 100%
* coverage in `__tests__/api-routes.test.ts`.
*
* ⚠️ **THIS MODULE IS NO LONGER IMPORT-FREE (#1748).** It imports exactly one thing —
* `RESERVED_PLATFORM_PREFIXES` — from `@zion/sdk-contracts`, and re-exports it, because that list
* grew a consumer that lives in `@zion/sdk-contracts` and this package already depends on it
* (`workspace:*`), so down is the only direction that does not create a cycle. Everything else here
* still takes primitive arguments rather than importing manifest types; see {@link DeviceMtlsInput}.
*/
/**
* The shape every apiPath must match.
*
* - Starts with `/v1/`
* - Has at least one further segment (rejects bare `/v1` — would tie with
*   the static `/v1 → core-api` rule on Gateway-API creation timestamp,
*   producing nondeterministic routing).
* - Subsequent segments may contain only URL-safe characters
*   (`[A-Za-z0-9_.~-]`).
* - Optional single trailing slash (normalized away by `normalizePrefix`).
*
* Aligned with the JSON-schema `pattern` in
* `cloud/platform-sdk/schemas/fixture.schema.json`; keep in sync.
*/
const API_PATH_PATTERN = /^\/v1\/[A-Za-z0-9_.~-]+(\/[A-Za-z0-9_.~-]+)*\/?$/;
/** Maximum apiPaths entries per service. */
const API_PATHS_MAX_PER_SERVICE = 32;
/** Maximum apiPaths entries summed across all services in one app. */
const API_PATHS_MAX_PER_APP = 128;
/**
* Strip a single trailing slash. `/v1/` stays as `/v1` for consistency.
* Idempotent. Single-character `/` is preserved (never reached in practice
* because `API_PATH_PATTERN` rejects bare `/`).
*/
function normalizePrefix(prefix) {
	return prefix.length > 1 && prefix.endsWith("/") ? prefix.slice(0, -1) : prefix;
}
/**
* Throws with a clear message if `prefix` doesn't match `API_PATH_PATTERN`.
* `fixtureId` is included in the error message for actionable diagnostics.
*/
function validatePrefix(prefix, fixtureId) {
	if (!API_PATH_PATTERN.test(prefix)) throw new Error(`fixture '${fixtureId}': apiPath '${prefix}' is invalid. Must match ${API_PATH_PATTERN} — start with /v1/, have at least one further segment, contain only [A-Za-z0-9_.~-] characters.`);
}
/**
* True iff one prefix is a path-segment prefix of the other (or equal).
* Segment-aware: `/v1/foo` and `/v1/foobar` do NOT overlap.
*/
function prefixesOverlap(a, b) {
	if (a === b) return true;
	const longer = a.length > b.length ? a : b;
	const shorter = a.length > b.length ? b : a;
	return longer.startsWith(shorter + "/");
}
/**
* True iff a pathname starts at a segment boundary of the given prefix.
*
* `matchesPrefix('/v1/contexts', '/v1/contexts')` → true
* `matchesPrefix('/v1/contexts/abc', '/v1/contexts')` → true
* `matchesPrefix('/v1/contexts-other', '/v1/contexts')` → false
* `matchesPrefix('/v1/other', '/v1/contexts')` → false
*
* Used by the dev-shell MSW catch-all to decide passthrough vs 404.
*/
function matchesPrefix(pathname, prefix) {
	return pathname === prefix || pathname.startsWith(prefix + "/");
}
/**
* Throws if `prefix` collides with any entry in `RESERVED_PLATFORM_PREFIXES`.
*
* "Collides" means the candidate prefix is reserved itself OR is a
* sub-prefix of a reserved one OR a reserved one is a sub-prefix of it.
* E.g. `/v1/auth/foo` collides with reserved `/v1/auth`; and the candidate
* `/v1` (impossible per pattern, but defensively) would also collide with
* every reserved entry.
*/
function rejectReservedOverlap(prefix, fixtureId) {
	for (const reserved of RESERVED_PLATFORM_PREFIXES) if (prefixesOverlap(prefix, reserved)) throw new Error(`fixture '${fixtureId}': apiPath '${prefix}' overlaps platform-reserved prefix '${reserved}'. App fixtures cannot claim platform-owned paths.`);
}
/** The two supported device-mTLS edge postures (DEC-4/DEC-6). */
const DEVICE_MTLS_MODES = ["forward", "verify"];
/**
* `caCertRef` grammar — an RFC-1123 DNS label (≤ 63 chars, lower-alphanumeric
* with internal hyphens). App-chosen; the operator embeds it in a derived
* Kubernetes resource name for the replicated factory-CA ConfigMap (DEC-9),
* so it must be name-safe. Kept as a constant so every layer validates the
* same grammar.
*/
const CA_CERT_REF_PATTERN = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
/**
* Validate one service's `deviceMtls` block against the DEC-6 contract.
* Pure; throws `Error` with an actionable, `fixtureId`-named message on the
* FIRST violation (mirrors `validatePrefix` / `rejectReservedOverlap`).
*
* The single source of truth for the four DEC-6 rules, called by all three
* defense layers — CP publish (`@zion/release-engine-v3` bundle-parser),
* `zion-app validate-app` (`@zion/sdk-node`), and operator reconcile
* (`@zion/zion-operator`) — so they can never drift.
*
* Rules (in order):
*   1. `mode` is one of `DEVICE_MTLS_MODES`.
*   2. `apiPath` matches `API_PATH_PATTERN` (shape — schema also enforces;
*      defense-in-depth for the operator, which reads the raw manifest).
*   3. `apiPath` (normalized) does not overlap a `RESERVED_PLATFORM_PREFIXES`
*      entry. Self-contained: kept here so every caller is safe even though
*      rule 4 makes it transitively redundant at publish.
*   4. `apiPath` (normalized) is a member of `serviceApiPaths` (normalized) —
*      deviceMtls partitions the declared apiPaths; it never invents a route.
*      This is ALSO how DEC-6's "no cross-app overlap" is satisfied: apiPaths
*      are already non-overlapping across fixtures/apps, so a device apiPath
*      inherits that guarantee (see plan §0.5).
*   5. `caCertRef` present + matches `CA_CERT_REF_PATTERN` iff `mode` is
*      'verify'; absent iff `mode` is 'forward'.
*   6. The service does NOT also declare `topology.scaleToZero` (DEC-24) — a
*      device-mTLS service holds long-lived MQTT-over-WSS connections and needs
*      a ≥1 replica floor. The operator floors it at runtime (WS-A6); this is
*      the fail-loud publish / validate-app DX gate. Passed as a boolean so
*      this module never imports the manifest topology type.
*
* @param deviceMtls              the service's `deviceMtls` block.
* @param serviceApiPaths         THIS service's `apiPaths` (raw; normalized here).
* @param serviceHasScaleToZero   true iff the SAME service declares
*                                `topology.scaleToZero` (DEC-24 rule 6).
* @param fixtureId               for actionable diagnostics.
*/
function validateDeviceMtls(deviceMtls, serviceApiPaths, serviceHasScaleToZero, fixtureId) {
	const { apiPath, mode, caCertRef } = deviceMtls;
	if (mode !== "forward" && mode !== "verify") throw new Error(`fixture '${fixtureId}': deviceMtls.mode '${mode}' is invalid. Must be one of ${JSON.stringify(DEVICE_MTLS_MODES)}.`);
	if (!API_PATH_PATTERN.test(apiPath)) throw new Error(`fixture '${fixtureId}': deviceMtls.apiPath '${apiPath}' is invalid. Must match ${API_PATH_PATTERN}.`);
	const normalized = normalizePrefix(apiPath);
	rejectReservedOverlap(normalized, fixtureId);
	const declared = serviceApiPaths.map(normalizePrefix);
	if (!declared.includes(normalized)) throw new Error(`fixture '${fixtureId}': deviceMtls.apiPath '${apiPath}' is not one of the service's apiPaths [${declared.join(", ")}]. deviceMtls partitions a declared apiPath onto the device-mTLS listener; add '${normalized}' to this service's apiPaths (and to expose.public).`);
	if (mode === "verify") {
		if (caCertRef === void 0 || caCertRef.length === 0) throw new Error(`fixture '${fixtureId}': deviceMtls.mode is 'verify' but caCertRef is missing. verify mode requires a factory-CA reference (DEC-9).`);
		if (!CA_CERT_REF_PATTERN.test(caCertRef)) throw new Error(`fixture '${fixtureId}': deviceMtls.caCertRef '${caCertRef}' is invalid. Must match ${CA_CERT_REF_PATTERN} (an RFC-1123 label).`);
	} else if (caCertRef !== void 0) throw new Error(`fixture '${fixtureId}': deviceMtls.mode is 'forward' but caCertRef is set. forward mode holds no CA — remove caCertRef.`);
	if (serviceHasScaleToZero) throw new Error(`fixture '${fixtureId}': a service declaring deviceMtls cannot also declare topology.scaleToZero — device-mTLS holds long-lived MQTT-over-WSS connections and needs a >=1 replica floor. Remove scaleToZero (use singleton or active-active).`);
}
//#endregion
export { API_PATHS_MAX_PER_APP, API_PATHS_MAX_PER_SERVICE, API_PATH_PATTERN, CA_CERT_REF_PATTERN, DEVICE_MTLS_MODES, RESERVED_PLATFORM_PREFIXES, matchesPrefix, normalizePrefix, prefixesOverlap, rejectReservedOverlap, validateDeviceMtls, validatePrefix };

//# sourceMappingURL=api-routes.js.map